import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Divider,
  Pagination,
  Spin,
  Typography,
  Dropdown,
  Button,
  Empty,
  Flex,
} from "antd";
import type { MenuProps } from "antd";
import { DownOutlined, MailOutlined, PhoneOutlined } from "@ant-design/icons";
import { JobApplication } from "../../../../Entities/JobApplication";
import { User } from "../../../../Entities/User";
import jobApplicationService from "../../../../Service/job.application.service";
import userService from "../../../../Service/user.service";
import { PaginationType } from "../../../../Interface";
import {
  capitalizeFirstLetter,
  convertTime,
  getInitials,
} from "../../../../Utils/generic-functions";
import { useNavigate } from "react-router-dom";
import {
  ApplicationFilterType,
  ApplicationCounts,
  APPLICATION_FILTERS,
  FilterOption,
} from "./ApplicationListing.types";
import "./ApplicationListing.scss";

interface ApplicationListingProps {
  applicationId: string;
  selectedApplication: JobApplication | null;
  setSelectedApplication: React.Dispatch<
    React.SetStateAction<JobApplication | null>
  >;
}

// Default counts object
const defaultCounts: ApplicationCounts = {
  all: 0,
  profileQualified: 0,
  profileUnqualified: 0,
  profilePending: 0,
  aiQualified: 0,
  aiUnqualified: 0,
  aiPending: 0,
  progressed: 0,
  shortlisted: 0,
  interview: 0,
  hired: 0,
  rejected: 0,
};

const ApplicationListing: React.FC<ApplicationListingProps> = ({
  applicationId,
  setSelectedApplication,
  selectedApplication,
}) => {
  const navigate = useNavigate();

  // State
  const [userApplications, setUserApplications] = useState<JobApplication[]>(
    [],
  );
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationType>({
    page: 1,
    limit: 10,
    totalPages: 0,
    totalRecords: 0,
  });
  const [activeFilter, setActiveFilter] =
    useState<ApplicationFilterType>("all");
  const [applicationCounts, setApplicationCounts] =
    useState<ApplicationCounts>(defaultCounts);
  const [hasScrolledToSelected, setHasScrolledToSelected] = useState(false);
  const [isFindingPage, setIsFindingPage] = useState(false);
  const [userDetails, setUserDetails] = useState<User | null>(null);
  // Refs
  const applicationsRef = useRef<HTMLDivElement>(null);
  const selectedApplicationRef = useRef<HTMLDivElement>(null);
  const isFilterChangeRef = useRef(false);
  const previousPageRef = useRef<number>(1);

  // Fetch full user details when selected application's user changes
  useEffect(() => {
    const userId = selectedApplication?.user?._id;
    if (!userId) return;
    userService.getUserById(userId).then((user) => {
      if (user) setUserDetails(user);
    });
  }, [selectedApplication?.user?._id]);

  // Fetch applications with filter
  const fetchUserApplications = useCallback(
    async (
      page: number,
      userId: string,
      filter: ApplicationFilterType = "all",
    ) => {
      setApplicationsLoading(true);
      try {
        const response =
          await jobApplicationService.getAllJobApplicationsByuserId(
            userId,
            page,
            pagination.limit,
            filter, // Pass filter to API
          );

        setUserApplications(response.data || []);
        setPagination((prev) => ({
          ...prev,
          page: response.currentPage,
          totalPages: response.totalPages,
          totalRecords: response.total,
        }));

        // Update counts from API response
        if (response.counts) {
          setApplicationCounts(response.counts);
        }
      } catch (error) {
        console.error("Error fetching applications:", error);
        setUserApplications([]);
      } finally {
        setApplicationsLoading(false);
      }
    },
    [pagination.limit],
  );

  // Handle filter change
  const handleFilterChange = useCallback((filter: ApplicationFilterType) => {
    isFilterChangeRef.current = true; // Mark that this is a filter change
    setActiveFilter(filter);
    setPagination((prev) => ({ ...prev, page: 1 })); // Reset to page 1 on filter change
  }, []);

  // Effect: Fetch when filter changes or initial load (always fetches page 1)
  useEffect(() => {
    if (selectedApplication?.user?._id) {
      fetchUserApplications(1, selectedApplication.user._id, activeFilter);
      isFilterChangeRef.current = false; // Reset flag after fetch
      // Don't update previousPageRef here - let the pagination effect handle it
    }
  }, [activeFilter, selectedApplication?.user?._id, fetchUserApplications]);

  // Effect: Fetch on page change (handles all page changes including going back to page 1)
  useEffect(() => {
    if (
      selectedApplication?.user?._id &&
      !isFindingPage &&
      !isFilterChangeRef.current && // Skip if this page change was due to filter change
      previousPageRef.current !== pagination.page // Only fetch if page actually changed
    ) {
      fetchUserApplications(
        pagination.page,
        selectedApplication.user._id,
        activeFilter,
      );
      previousPageRef.current = pagination.page; // Update previous page ref
    } else if (isFilterChangeRef.current) {
      // If filter change caused page reset, update the ref to prevent duplicate fetch
      previousPageRef.current = pagination.page;
      isFilterChangeRef.current = false;
    }
  }, [
    pagination.page,
    selectedApplication?.user?._id,
    activeFilter,
    isFindingPage,
    fetchUserApplications,
  ]);

  // Effect: Handle finding and loading the correct page when selectedApplication changes
  useEffect(() => {
    if (
      selectedApplication?.user &&
      selectedApplication?.user._id &&
      applicationId &&
      !isFindingPage
    ) {
      // Check if the selected application is on the current page
      const isOnCurrentPage = userApplications.some(
        (app) => app._id === applicationId,
      );

      if (!isOnCurrentPage && userApplications.length > 0) {
        // Application is not on current page, try to find it
        setIsFindingPage(true);
        // For now, just load first page with current filter
        // Finding exact page across filters is complex, so we'll load page 1
        fetchUserApplications(
          1,
          selectedApplication.user._id,
          activeFilter,
        ).then(() => {
          setIsFindingPage(false);
        });
      } else if (userApplications.length === 0) {
        // No applications loaded yet, load the first page
        setIsFindingPage(true);
        fetchUserApplications(
          1,
          selectedApplication.user._id,
          activeFilter,
        ).then(() => {
          setIsFindingPage(false);
        });
      }
    } else if (
      selectedApplication?.user &&
      selectedApplication?.user._id &&
      !applicationId &&
      !isFindingPage
    ) {
      // No specific application selected, just load the first page
      fetchUserApplications(1, selectedApplication.user._id, activeFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApplication, applicationId]);

  // Effect: Scroll to selected application when it appears in the list
  useEffect(() => {
    if (
      selectedApplicationRef.current &&
      userApplications.length > 0 &&
      applicationId &&
      !hasScrolledToSelected &&
      !applicationsLoading
    ) {
      const timer = setTimeout(() => {
        const isInList = userApplications.some(
          (app) => app._id === applicationId,
        );
        if (isInList && selectedApplicationRef.current) {
          selectedApplicationRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          setHasScrolledToSelected(true);
        }
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [
    userApplications,
    applicationId,
    hasScrolledToSelected,
    applicationsLoading,
  ]);

  // Reset scroll flag when applicationId changes
  useEffect(() => {
    setHasScrolledToSelected(false);
  }, [applicationId]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  // Handle application click
  const handleApplicationClick = (application: JobApplication) => {
    const jobId = application.jobPost?._id;
    const appId = application._id;

    if (jobId && appId) {
      navigate(`/application-details?applicationId=${appId}&jobId=${jobId}`, {
        replace: true,
      });
      setSelectedApplication(application);
    }
  };

  // Get current filter option
  const getCurrentFilterOption = (): FilterOption => {
    return (
      APPLICATION_FILTERS.find((f) => f.key === activeFilter) ||
      APPLICATION_FILTERS[0]
    );
  };

  // Build dropdown menu items
  const getFilterMenuItems = (): MenuProps["items"] => {
    return APPLICATION_FILTERS.map((filterOption: FilterOption) => {
      const count = applicationCounts[filterOption.countKey] || 0;

      return {
        key: filterOption.key,
        label: (
          <div className="application-listing-dropdown-item">
            <span className="application-listing-dropdown-label">
              {filterOption.label}
            </span>
            <span className="application-listing-dropdown-count">
              ({count})
            </span>
          </div>
        ),
        title: filterOption.tooltip,
        className:
          filterOption.key === activeFilter
            ? "application-listing-dropdown-item-selected"
            : "",
      };
    });
  };

  // Handle dropdown menu click
  const handleMenuClick = ({ key }: { key: string }) => {
    handleFilterChange(key as ApplicationFilterType);
  };

  return (
    <div className="application-listing-container">
      {/* Header */}
      <div className="application-listing-header">
        <span className="user-avatar">
          {getInitials(
            userDetails?.firstName ?? "",
            userDetails?.lastName ?? "",
          )}
        </span>
        <div className="application-listing-header-content">
          <Typography.Title level={5} style={{ margin: 0, fontSize: 14 }}>
            {capitalizeFirstLetter(userDetails?.firstName ?? "")}{" "}
            {capitalizeFirstLetter(userDetails?.lastName ?? "")}
          </Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            <MailOutlined style={{ marginRight: 4 }} />
            {userDetails?.email}
          </Typography.Text>
          {userDetails?.mobileNumber && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              <PhoneOutlined style={{ marginRight: 4 }} />
              {userDetails.mobileNumber}
            </Typography.Text>
          )}
        </div>
      </div>

      <Divider style={{ margin: 0 }} />

      {/* Filter Section */}
      <div className="application-listing-filter">
        <Dropdown
          menu={{
            items: getFilterMenuItems(),
            onClick: handleMenuClick,
          }}
          trigger={["hover", "click"]}
          placement="bottomLeft"
        >
          <Button type="default" className="application-listing-filter-button">
            <span className="application-listing-filter-label">
              {getCurrentFilterOption().label}
            </span>
            <span className="application-listing-filter-count">
              ({applicationCounts[getCurrentFilterOption().countKey] || 0})
            </span>
            <DownOutlined className="application-listing-filter-icon" />
          </Button>
        </Dropdown>
      </div>

      {/* Applications List */}
      <div className="application-listing-applications">
        <div
          className="application-listing-applications-list"
          ref={applicationsRef}
        >
          {applicationsLoading ? (
            <div className="application-listing-loader">
              <Spin size="default" />
            </div>
          ) : userApplications.length === 0 ? (
            <div className="application-listing-empty">
              <Empty description="No applications found for this filter." />
            </div>
          ) : (
            userApplications.map((application) => {
              const isSelected = application?._id === applicationId;
              return (
                <div
                  key={application._id}
                  ref={isSelected ? selectedApplicationRef : null}
                  className={`${
                    application?._id === selectedApplication?._id
                      ? "application-listing-application-item-selected"
                      : "application-listing-application-item"
                  }`}
                  onClick={() => handleApplicationClick(application)}
                >
                  <Flex align="start" vertical gap={4}>
                    <Typography.Text
                      className="application-listing-item-title"
                      style={{ fontSize: 14, marginBottom: 0 }}
                    >
                      {application?.jobPost?.title}
                    </Typography.Text>
                    <Typography.Text
                      className="application-listing-item-subtitle"
                      type="secondary"
                      style={{ fontSize: 10, marginBottom: 0 }}
                    >
                      Applied {convertTime(application.applied_date)}
                    </Typography.Text>
                  </Flex>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        <div className="application-listing-pagination">
          <Pagination
            showSizeChanger={false}
            hideOnSinglePage={true}
            size="small"
            current={pagination.page}
            total={pagination.totalRecords}
            pageSize={pagination.limit}
            onChange={handlePageChange}
            disabled={applicationsLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default ApplicationListing;
